import classNames from 'classnames'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import React, {useEffect, useRef} from 'react'

import {makeEditable, tailwindConfig} from '@/common/utils'
import Content from '@/components/Content'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import KeyFiguresItem from '@/components/KeyFigures/KeyFiguresItem'
import Testimonial from '@/components/Testimonial'

const KeyFigures = ({
  backgroundColors,
  headingTag,
  heading,
  content,
  testimonial,
  items = [],
  _editable,
}) => {
  const HeadingTag = headingTag || 'p'

  const listRef = useRef()
  const testimonialRef = useRef()
  const containerRef = React.useRef()

  useEffect(() => {
    const section = listRef.current
    const testimonial = testimonialRef.current ? testimonialRef.current.children[0] : null
    if (section && testimonial) {
      const mm = gsap.matchMedia()
      mm.add(`(min-width: ${tailwindConfig.theme.screens['lg']})`, () => {
        ScrollTrigger.create({
          pin: testimonial,
          pinSpacing: false,
          trigger: testimonial,
          endTrigger: section,
          start: 'bottom+=48 bottom',
          end: 'bottom+=48 bottom',
        })
      })
      return () => mm.revert()
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      const context = gsap.context(() => {
        gsap.fromTo(
          'path',
          {drawSVG: '0%'},
          {
            drawSVG: '100%',
            scrollTrigger: {
              trigger: container,
              start: 'center bottom',
            },
          }
        )
      }, container)
      return () => context.revert()
    }
  }, [])

  return (
    <section
      ref={containerRef}
      {...makeEditable(_editable)}
      className={classNames('', backgroundColors.previous)}
    >
      <div
        className={classNames(
          'relative overflow-hidden rounded-t-[theme(spacing.20)] py-20 md:py-18 lg:py-32',
          backgroundColors.current
        )}
      >
        <div
          className={classNames(
            'absolute -top-81 h-full w-full bg-[radial-gradient(circle_at_top,#F6FF8C,transparent,transparent,transparent,transparent)] blur-[120px] md:bg-[radial-gradient(circle_at_top,#F6FF8C,#F6FF8C,transparent,transparent,transparent)]'
          )}
        />
        <Grid>
          <HeadingTag className='z-50 col-span-full row-start-1 md:col-span-7 lg:col-start-4'>
            <Heading theme='light' heading={heading} className='text-center' />
          </HeadingTag>
          <Content
            content={content}
            theme='light'
            className='z-50 col-span-full row-start-2 mt-6 text-center md:col-span-4 md:col-start-2 lg:col-span-7 lg:col-start-4'
            size='l'
          />
          <DoodleArrow aria-hidden className='absolute left-1/3 top-1/4 md:left-3/4 md:top-50' />
          <div
            ref={testimonialRef}
            className='col-span-full mt-20 h-full w-full md:col-span-5 md:mt-12 lg:col-span-4 lg:mt-48'
          >
            {testimonial && <Testimonial key={testimonial._uid} {...testimonial.content} />}
          </div>
          {items.length > 0 && (
            <ul
              ref={listRef}
              className='col-span-full row-start-3 mt-48 grid grid-cols-2 gap-8 gap-y-14 md:col-span-7 lg:col-span-6 lg:col-start-6'
            >
              {items.map((props) => (
                <KeyFiguresItem key={props._uid} {...props} />
              ))}
            </ul>
          )}
        </Grid>
      </div>
    </section>
  )
}

const DoodleArrow = (props) => (
  <svg
    width='104'
    height='136'
    viewBox='0 0 104 136'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <g clipPath='url(#clip0_893_8741)'>
      <path
        d='M2 18.55C16.749 20.3756 31.8198 23.613 44.8315 31.0449C57.796 38.4498 66.6866 50.2335 73.5119 63.3383C80.4717 76.7011 85.8672 90.9923 89.9761 105.464M79.9221 105.787C82.5372 108.652 85.2154 111.612 88.2578 114.036C89.0195 114.643 90.6242 115.549 91.2172 116.296C92.494 117.905 98.8811 109.406 100.312 108.536'
        stroke='#F6FF8C'
        strokeWidth='2'
        strokeLinecap='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_893_8741'>
        <rect width='104' height='136' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

export default KeyFigures
